import React, { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Clock3, AlertCircle } from "lucide-react";
import { GlobalContext } from "../../../Context/GlobalContext";
import useClients from "../../Services/ClientServices/ClientConfig/useClientConfig";
/* ---------------- CONSTANTS & STYLES ---------------- */

const THEME_BLUE_BG = "bg-[#f8fbff]";
const BORDER_SMART = "border-gray-300";

const statusColors = {
  Y: {
    border: "border-emerald-200",
    bg: "bg-emerald-50/50",
    text: "text-emerald-800",
    badgeBg: "bg-emerald-100",
    badgeText: "text-emerald-700",
  },
  N: {
    border: "border-gray-300",
    bg: "bg-white",
    text: "text-rose-700",
    badgeBg: "bg-rose-50",
    badgeText: "text-rose-700",
  },
};

/* ---------------- COMPONENT ---------------- */

export default function ClientOnboardingTab({ clientId, clientName }) {
  const { fetchClientOnboardingStatus } = useClients();

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [previewImage, setPreviewImage] =
    useState(null);

  useEffect(() => {
    if (!clientId) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");

      const data = await fetchClientOnboardingStatus(clientId);

      if (!cancelled) {
        setItems(data || []);
        setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const completedCount = useMemo(
    () => items.filter((i) => i.config_status === "Y").length,
    [items]
  );

  const totalCount = items.length || 0;
  const percent = totalCount ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <div className={`w-full min-h-[calc(100vh-120px)] ${THEME_BLUE_BG}`}>
      {/* PAGE HEADER */}
      <div className={`border-b bg-white px-6 py-6 ${BORDER_SMART}`}>
        <div className="flex items-center gap-3">
          <div className="grid h-12 w-12 place-items-center rounded-xl bg-[#01C37D]/10">
            <CheckCircle2 className="text-[#01C37D]" size={22} />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Onboarding Checklist</h1>
            <p className="text-sm text-gray-500">
              {clientName || "—"} • <span className="font-mono">{clientId}</span>
            </p>
          </div>
        </div>
      </div>

      {/* CONTENT */}
      <div className="px-0 py-8 max-w-[1800px] space-y-8">

        {/* PROGRESS CARD */}
        <section className={`rounded-2xl border ${BORDER_SMART} bg-white p-6 shadow-sm`}>
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-4">
              <div className="grid h-12 w-12 place-items-center rounded-xl bg-[#f8fbff] border border-gray-200">
                <Clock3 size={22} className="text-[#01C37D]" />
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-gray-400">
                  Overall completion
                </div>
                <div className="text-lg font-bold text-gray-900">
                  {completedCount} of {totalCount} steps completed
                </div>
              </div>
            </div>

            <div className="w-full max-w-xs">
              <div className="mb-2 flex items-center justify-between text-xs">
                <span className="font-bold text-gray-500 uppercase">Progress</span>
                <span className="font-bold text-[#01C37D]">{percent}%</span>
              </div>
              <div className="h-3 overflow-hidden rounded-full bg-gray-100 border border-gray-200">
                <div
                  className="h-full rounded-full bg-[#01C37D] transition-all duration-700 ease-out shadow-[0_0_12px_rgba(1,195,125,0.3)]"
                  style={{ width: `${percent}%` }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* STATUS LIST */}
        <section className="space-y-4">
          <div className="flex items-center gap-2 px-1">
            <div className="h-1.5 w-1.5 rounded-full bg-[#01C37D]" />
            <h2 className="text-xs font-bold uppercase tracking-widest text-gray-500">
              Configuration Status
            </h2>
          </div>

          {loading && (
            <div className="py-20 flex flex-col items-center justify-center bg-white rounded-2xl border border-dashed border-gray-300">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#01C37D]/20 border-t-[#01C37D]" />
              <p className="mt-4 text-sm font-medium text-gray-400">Syncing checklist...</p>
            </div>
          )}

          {!loading && error && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-4 flex items-center gap-3 text-sm text-rose-700 font-medium">
              <AlertCircle size={18} />
              {error}
            </div>
          )}

          {!loading && !error && items.length === 0 && (
            <div className="py-20 text-center bg-white rounded-2xl border border-dashed border-gray-300">
              <p className="text-sm font-medium text-gray-400">No configuration records found.</p>
            </div>
          )}

          {!loading && !error && items.length > 0 && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-5">
              {items.map((item, idx) => {
                const status = item.config_status === "Y" ? "Y" : "N";
                const c = statusColors[status];

                return (
                  <div
                    key={`${item.config_type}-${idx}`}
                    className={`flex flex-col justify-between rounded-2xl border ${c.border} ${c.bg} p-5 transition-all hover:shadow-md group`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex gap-3">
                        <div className={`mt-0.5 grid h-5 w-5 place-items-center rounded-md border ${status === 'Y' ? 'bg-[#01C37D] border-[#01C37D]' : 'bg-white border-gray-300'}`}>
                          {status === 'Y' && <CheckCircle2 size={12} className="text-white" />}
                        </div>
                        <div>
                          <div className={`text-[13px] font-bold uppercase tracking-tight ${status === 'Y' ? 'text-gray-900' : 'text-gray-500'}`}>
                            {item.config_type || "Undefined Step"}
                          </div>
                          <div className="mt-1">

                            {item.config_value ? (

                              /^(https?:\/\/)/i.test(
                                item.config_value
                              ) ? (

                                <button
                                  onClick={() =>
                                    setPreviewImage(
                                      item.config_value
                                    )
                                  }
                                  className="rounded-lg border border-[#01C37D] bg-[#01C37D]/10 px-3 py-1 text-xs font-semibold text-[#01C37D] transition hover:bg-[#01C37D] hover:text-white"
                                >
                                  View
                                </button>

                              ) : (

                                <div className="text-xs font-medium text-gray-500 break-words whitespace-normal">
                                  {item.config_value}
                                </div>

                              )

                            ) : (

                              <div className="text-xs text-gray-400">
                                Pending configuration
                              </div>

                            )}

                          </div>
                        </div>
                      </div>

                      <span className={`shrink-0 rounded-lg px-2.5 py-1 text-[10px] font-bold uppercase tracking-tighter shadow-sm border ${c.badgeBg} ${c.badgeText}`}>
                        {status === "Y" ? "Completed" : "Action Required"}
                      </span>
                    </div>

                    <div className={`h-1 w-full rounded-full bg-black/5 overflow-hidden`}>
                      <div className={`h-full ${status === 'Y' ? 'bg-[#01C37D]' : 'bg-gray-200'} transition-all`} style={{ width: status === 'Y' ? '100%' : '15%' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
      {previewImage && (

        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 p-4">

          <div className="relative max-w-3xl rounded-2xl bg-white p-4 shadow-2xl">

            {/* close */}
            <button
              onClick={() =>
                setPreviewImage(null)
              }
              className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
            >
              ✕
            </button>

            {/* image */}
            <img
              src={previewImage}
              alt="Preview"
              className="max-h-[80vh] rounded-xl object-contain"
            />

          </div>

        </div>
      )}
    </div>
  );
}